import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";

interface AddedTextItem {
  text_spot: string;
  text_added: string;
  sentence_number: number;
  amendment_id: string;
  id_long: string;
}

interface DeletedTextItem {
  text_spot: string;
  sentence_number: number;
  amendment_id: string;
  id_long: string;
  text_deleted: string;
  word_before: string;
}

interface ReplacedTextItem {
  text_replaced: string;
  text_added: string;
  sentence_number: number;
  amendment_id: string;
  id_long: string;
}

interface Amendment {
  amendment_id: string;
  paragraph_id: string;
  paragraph_number: number;
  id_long: string;
  article_number: number;
}

interface HighlightedParagraphProps {
  text: string;
  textAlign?: string;
  amendments?: Amendment[];
  added_text?: AddedTextItem[] | string;
  replaced_text?: ReplacedTextItem[] | string;
  deleted_text?: DeletedTextItem[] | string;
  fileId?: string;
  new_paragraph?: boolean;
  article_name?: string;
  paragraph_origin_id_long?: string;
}

const ParagraphContainer = styled.div<{ textAlign?: string }>`
  text-align: ${(props) => props.textAlign || "left"};
  margin-bottom: 1em;
  color: #6e6e6e;
  font-family: "Sharp Sans";
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  position: relative; // Zunae
`;

const AddedText = styled.span`
  background-color: #a3ffb0; /* Light green background */
  color: #006400; /* Dark green text */
  font-weight: 500;
  font-family: "Sharp Sans";
  font-size: 14px;
  position: relative;
  cursor: pointer;
`;

const DeletedText = styled.span`
  background-color: #ffcccc; /* Light red background */
  color: #cc0000; /* Dark red text */
  font-weight: 500;
  text-decoration: line-through;
  font-family: "Sharp Sans";
  font-size: 14px;
  position: relative;
  cursor: pointer;
`;

const Tooltip = styled.div<{ isVisible: boolean }>`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: #333;
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 12px;
  z-index: 100;
  white-space: nowrap;
  display: ${(props) => (props.isVisible ? "block" : "none")};
  margin-bottom: 5px;
  max-width: 300px;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: #333 transparent transparent transparent;
  }
`;

const SwitchIcon = styled.span`
  margin-left: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  font-size: 10px;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.4);
  }
`;

const TooltipContent = styled.div`
  display: flex;
  align-items: center;
`;

const IdLink = styled.span`
  cursor: pointer;
  text-decoration: underline;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const DeletedContent = styled.span`
  background-color: #ffcccc;
  color: #cc0000;
  text-decoration: line-through;
  margin-left: 4px;
  padding: 0 4px;
  border-radius: 2px;
  font-family: "Sharp Sans";
  font-size: 14px;
  display: inline-block;
  position: relative;
  cursor: pointer;
`;

const DeleteButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-left: 4px;
  padding: 0;
  //border: 1px solid #cc0000;
  border-radius: 4px;
  //background-color: #ffcccc;
  color: #cc0000;
  cursor: pointer;
  font-size: 15px;
  font-weight: 900;
  transition: all 0.2s;
  vertical-align: middle;
  border: none;
  background-color: transparent;

  &:hover {
    background-color: #ff9999;
  }
`;

const HTMLTableWrapper = styled.div<{ textAlign?: string }>`
  text-align: ${(props) => props.textAlign || "left"};
  margin-bottom: 1em;
  color: #6e6e6e;
  font-family: "Sharp Sans";
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  position: relative;

  table {
    width: 100%;
    border-collapse: collapse;
    border-color: #ebebeb;
  }

  th,
  td {
    border: 1px solid #ebebeb;
    padding: 8px;
    text-align: left;

    /* Preserve inline styles from the HTML content */
    &[style] {
      /* Let inline styles override when needed */
    }
  }

  th {
    background-color: #f5f5f5;
    font-weight: 600;
  }

  /* Support for center alignment from HTML */
  td[align="center"],
  th[align="center"] {
    text-align: center;
  }

  td[align="left"],
  th[align="left"] {
    text-align: left;
  }

  td[align="right"],
  th[align="right"] {
    text-align: right;
  }
`;

const HighlightedParagraph: React.FC<HighlightedParagraphProps> = ({
  text,
  textAlign,
  added_text,
  replaced_text,
  deleted_text,
  amendments,
  new_paragraph,
  fileId,
  article_name,
  paragraph_origin_id_long,
}) => {
  const navigate = useNavigate();
  const [hoveredItemKey, setHoveredItemKey] = useState<string | null>(null);
  const [showingOriginalText, setShowingOriginalText] = useState<
    Record<string, boolean>
  >({});
  const [showingDeletedText, setShowingDeletedText] = useState<
    Record<string, boolean>
  >({});
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef<HTMLSpanElement>(null);

  // Move normalizeString function definition here, before it's used
  const normalizeString = (str: string) =>
    str
      .normalize("NFC")
      .replace(/[\u2018\u2019\u201A\u201B\u2032\u2035]/g, "'") // normalize apostrophes
      .replace(/\u00A0/g, " ") // replace non-breaking spaces
      .replace(/\s+/g, " ") // collapse whitespace
      .trim();

  // Function to detect if content is HTML
  const isHTML = (str: string): boolean => {
    // Check if content contains HTML entities (escaped HTML)
    const containsHTMLEntities =
      /&[a-z]+;/i.test(str) || str.includes("&lt;") || str.includes("&gt;");

    // If content contains HTML entities, decode them first
    let processedStr = str;
    if (containsHTMLEntities) {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = str;
      processedStr = tempDiv.textContent || tempDiv.innerText || "";
    }

    // Check for basic HTML pattern first
    const containsHTMLTags = /<[^>]+>/.test(processedStr);

    // Special handling for table fragments that need wrapping
    const isTableFragment = /^\s*<(tbody|thead|tfoot|tr)/i.test(
      processedStr.trim()
    );

    // If it's a table fragment, wrap it to test proper HTML structure
    let testStr = processedStr;
    if (isTableFragment) {
      testStr = `<table>${processedStr}</table>`;
    }

    const doc = new DOMParser().parseFromString(testStr, "text/html");
    const hasHTMLNodes = Array.from(doc.body.childNodes).some(
      (node) => node.nodeType === 1
    );

    // HTML is detected if it contains HTML tags OR has proper HTML nodes after wrapping
    const isHTMLContent = containsHTMLTags && (hasHTMLNodes || isTableFragment);

    // Enhanced debug logging (remove in production)
    if (process.env.NODE_ENV === "development") {
      console.log("HighlightedParagraph: HTML detection analysis", {
        originalContent: str.substring(0, 200) + "...",
        processedContent: processedStr.substring(0, 200) + "...",
        containsHTMLEntities,
        containsHTMLTags,
        isTableFragment,
        hasHTMLNodes,
        isHTMLContent,
        testStr: isTableFragment
          ? testStr.substring(0, 150) + "..."
          : "same as processed",
        bodyChildNodes: doc.body.childNodes.length,
        bodyHTML: doc.body.innerHTML.substring(0, 100),
      });
    }

    return isHTMLContent;
  };

  // Function to highlight HTML content while preserving structure
  const highlightHTMLContent = (
    htmlString: string,
    phrases: string[]
  ): React.ReactNode => {
    // Check if content contains HTML entities and decode if needed
    let decodedHTML = htmlString;
    const containsHTMLEntities =
      /&[a-z]+;/i.test(htmlString) ||
      htmlString.includes("&lt;") ||
      htmlString.includes("&gt;");

    if (containsHTMLEntities) {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlString;
      decodedHTML = tempDiv.textContent || tempDiv.innerText || "";
    }

    // Check if HTML needs table wrapper (for tbody, thead, tfoot fragments)
    const needsTableWrapper = /^\s*<(tbody|thead|tfoot|tr)/i.test(
      decodedHTML.trim()
    );
    const processedHTML = needsTableWrapper
      ? `<table>${decodedHTML}</table>`
      : decodedHTML;

    // Debug logging (remove in production)
    if (process.env.NODE_ENV === "development") {
      console.log("HighlightedParagraph: Processing HTML content", {
        containsHTMLEntities,
        needsTableWrapper,
        originalLength: htmlString.length,
        decodedLength: decodedHTML.length,
        processedLength: processedHTML.length,
        phrasesCount: phrases.length,
        originalPreview: htmlString.substring(0, 100),
        decodedPreview: decodedHTML.substring(0, 100),
        processedPreview: processedHTML.substring(0, 100),
      });
    }

    if (phrases.length === 0) {
      return (
        <HTMLTableWrapper
          textAlign={textAlign}
          dangerouslySetInnerHTML={{ __html: processedHTML }}
        />
      );
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(processedHTML, "text/html");

    // Function to process text nodes and apply highlighting
    const processTextNode = (textNode: Text): React.ReactNode[] => {
      const textContent = textNode.textContent || "";
      const highlightedContent = highlightText(textContent, phrases);

      // If highlightedContent is just a string, return it as is
      if (typeof highlightedContent === "string") {
        return [highlightedContent];
      }

      // If it's an array of React elements, return them
      return Array.isArray(highlightedContent)
        ? highlightedContent
        : [highlightedContent];
    };

    // Function to recursively process DOM nodes
    const processNode = (node: Node, key: string = ""): React.ReactNode => {
      if (node.nodeType === Node.TEXT_NODE) {
        const textResults = processTextNode(node as Text);
        return textResults.length === 1 ? textResults[0] : textResults;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        const tagName = element.tagName.toLowerCase();
        const attributes: { [key: string]: any } = {};

        // Copy attributes
        for (let i = 0; i < element.attributes.length; i++) {
          const attr = element.attributes[i];
          // Convert class to className for React
          const attrName = attr.name === "class" ? "className" : attr.name;
          attributes[attrName] = attr.value;
        }

        // Process children
        const children: React.ReactNode[] = [];
        for (let i = 0; i < node.childNodes.length; i++) {
          const childResult = processNode(node.childNodes[i], `${key}-${i}`);
          if (Array.isArray(childResult)) {
            children.push(...childResult);
          } else {
            children.push(childResult);
          }
        }

        // Create React element
        return React.createElement(
          tagName,
          { key: key, ...attributes },
          ...children
        );
      }
      return null;
    };

    // Process all child nodes of the body
    const processedContent: React.ReactNode[] = [];
    for (let i = 0; i < doc.body.childNodes.length; i++) {
      const result = processNode(doc.body.childNodes[i], `root-${i}`);
      if (result !== null) {
        if (Array.isArray(result)) {
          processedContent.push(...result);
        } else {
          processedContent.push(result);
        }
      }
    }

    return (
      <HTMLTableWrapper textAlign={textAlign}>
        {processedContent}
      </HTMLTableWrapper>
    );
  };

  // Parse JSON strings if needed
  const parseJsonIfString = <T,>(value: any): T[] => {
    if (!value) return [];

    if (typeof value === "string") {
      try {
        return JSON.parse(value);
      } catch (e) {
        console.error("Failed to parse JSON string:", e);
        return [];
      }
    }

    return Array.isArray(value) ? value : [];
  };

  // Ensure replaced_text and added_text are arrays
  const replacedTextArray = parseJsonIfString<ReplacedTextItem>(replaced_text);
  const addedTextArray = parseJsonIfString<AddedTextItem>(added_text);
  const deletedTextArray = parseJsonIfString<DeletedTextItem>(deleted_text);

  // Create a mapping of phrases to their corresponding items
  const phraseToItemMap = new Map<
    string,
    {
      id_long: string;
      type: "replaced" | "added" | "deleted";
      text_replaced?: string;
      text_added?: string;
      text_deleted?: string;
      word_before?: string;
      amendment_id: string;
    }
  >();

  replacedTextArray.forEach((item) => {
    const normalizedKey = normalizeString(item.text_added).toLowerCase();

    phraseToItemMap.set(normalizedKey, {
      id_long: item.id_long,
      type: "replaced",
      text_replaced: item.text_replaced,
      text_added: item.text_added,
      amendment_id: item.amendment_id,
    });
  });

  addedTextArray.forEach((item) => {
    const normalizedKey = normalizeString(item.text_added).toLowerCase();

    phraseToItemMap.set(normalizedKey, {
      id_long: item.id_long,
      type: "added",
      text_added: item.text_added,
      amendment_id: item.amendment_id,
    });
  });

  deletedTextArray.forEach((item) => {
    const normalizedKey = normalizeString(item.text_deleted).toLowerCase();

    phraseToItemMap.set(normalizedKey, {
      id_long: item.id_long,
      type: "deleted",
      text_deleted: item.text_deleted,
      word_before: item.word_before,
      amendment_id: item.amendment_id,
    });
  });

  // After creating phraseToItemMap

  const phrasesToHighlight = [
    ...replacedTextArray.map((item) => item.text_added),
    ...addedTextArray.map((item) => item.text_added),
    ...deletedTextArray
      .filter((item) => !item.word_before)
      .map((item) => item.text_deleted),
  ];

  const toggleOriginalText = (uniqueKey: string) => {
    setShowingOriginalText((prev) => ({
      ...prev,
      [uniqueKey]: !prev[uniqueKey],
    }));
  };

  const toggleDeletedText = (uniqueKey: string) => {
    setShowingDeletedText((prev) => ({
      ...prev,
      [uniqueKey]: !prev[uniqueKey],
    }));
  };

  const handleMouseEnter = (uniqueKey: string) => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    setHoveredItemKey(uniqueKey);
  };

  const handleMouseLeave = () => {
    tooltipTimeoutRef.current = setTimeout(() => {
      setHoveredItemKey(null);
    }, 300); // 300ms delay to allow moving to tooltip
  };

  const navigateToAmendment = (
    amendmentId: string,
    fileId: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    // Navigate to amendment page
    navigate(`/file/${fileId}/amendts/${amendmentId}`);
  };

  // Check if mouse is over tooltip to prevent it from closing
  useEffect(() => {
    const handleTooltipMouseEnter = () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
        tooltipTimeoutRef.current = null;
      }
    };

    const handleTooltipMouseLeave = () => {
      setHoveredItemKey(null);
    };

    const tooltipElement = tooltipRef.current;
    if (tooltipElement) {
      tooltipElement.addEventListener("mouseenter", handleTooltipMouseEnter);
      tooltipElement.addEventListener("mouseleave", handleTooltipMouseLeave);
    }

    return () => {
      if (tooltipElement) {
        tooltipElement.removeEventListener(
          "mouseenter",
          handleTooltipMouseEnter
        );
        tooltipElement.removeEventListener(
          "mouseleave",
          handleTooltipMouseLeave
        );
      }
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, [hoveredItemKey]);

  const calculateWordSimilarity = (text1: string, text2: string): boolean => {
    const normalizedText1 = normalizeString(text1);
    const normalizedText2 = normalizeString(text2);

    // If text2 is longer than text1, swap them to ensure we're looking for text2 within text1
    if (normalizedText2.length > normalizedText1.length) {
      return false;
    }

    // Use a sliding window to find the most similar substring
    const windowSize = normalizedText2.length;
    let minDistance = Infinity;

    for (let i = 0; i <= normalizedText1.length - windowSize; i++) {
      const substring = normalizedText1.substring(i, i + windowSize);
      const distance = levenshteinDistance(substring, normalizedText2);
      minDistance = Math.min(minDistance, distance);
    }

    // Calculate similarity ratio
    const similarityRatio = 1 - minDistance / windowSize;

    return similarityRatio > 0.8;
  };

  const levenshteinDistance = (str1: string, str2: string): number => {
    const m = str1.length;
    const n = str2.length;
    const dp: number[][] = Array(m + 1)
      .fill(null)
      .map(() => Array(n + 1).fill(0));

    // Initialize the first row and column
    for (let i = 0; i <= m; i++) {
      dp[i][0] = i;
    }
    for (let j = 0; j <= n; j++) {
      dp[0][j] = j;
    }

    // Fill in the rest of the matrix
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (str1[i - 1] === str2[j - 1]) {
          dp[i][j] = dp[i - 1][j - 1];
        } else {
          dp[i][j] = Math.min(
            dp[i - 1][j - 1] + 1, // substitution
            dp[i - 1][j] + 1, // deletion
            dp[i][j - 1] + 1 // insertion
          );
        }
      }
    }

    return dp[m][n];
  };

  const highlightText = (text: string, phrases: string[]) => {
    const normalizedText = normalizeString(text);

    if (phrases.length === 0) {
      return text;
    }

    // Get all items that need processing
    const deletedItemsWithWordBefore = Array.from(
      phraseToItemMap.values()
    ).filter((item) => item.type === "deleted" && item.word_before);

    // Collect all positions that need processing
    interface ProcessingItem {
      startIndex: number;
      endIndex: number;
      type: "word_before_deletion" | "phrase_match";
      item: any;
      originalPhrase?: string;
      matchLength?: number;
      similarityRatio?: number;
    }

    const processingItems: ProcessingItem[] = [];

    // Add word_before deletions
    deletedItemsWithWordBefore.forEach((item) => {
      const wordBefore = normalizeString(item.word_before || "");
      const wordBeforeIndex = normalizedText.indexOf(wordBefore);

      if (wordBeforeIndex !== -1) {
        processingItems.push({
          startIndex: wordBeforeIndex + wordBefore.length,
          endIndex: wordBeforeIndex + wordBefore.length,
          type: "word_before_deletion",
          item,
        });
      }
    });

    // Add phrase matches (excluding deleted items with word_before)
    const phrasesToCheck = phrases.filter((phrase) => {
      const normalizedPhrase = normalizeString(phrase).toLowerCase();
      const itemInfo = phraseToItemMap.get(normalizedPhrase);
      return !(itemInfo?.type === "deleted" && itemInfo.word_before);
    });

    phrasesToCheck.forEach((phrase) => {
      const normalizedPhrase = normalizeString(phrase);

      // Try exact matching first
      let index = normalizedText.indexOf(normalizedPhrase);
      if (index !== -1) {
        processingItems.push({
          startIndex: index,
          endIndex: index + normalizedPhrase.length,
          type: "phrase_match",
          item: phraseToItemMap.get(normalizedPhrase.toLowerCase()),
          originalPhrase: phrase,
          matchLength: normalizedPhrase.length,
          similarityRatio: 1,
        });
        return;
      }

      // If no exact match, try fuzzy matching
      const windowSize = Math.min(
        normalizedPhrase.length,
        normalizedText.length
      );
      let minDistance = Infinity;
      let bestStartIndex = -1;

      for (let i = 0; i <= normalizedText.length - windowSize; i++) {
        const substring = normalizedText.substring(i, i + windowSize);
        const distance = levenshteinDistance(substring, normalizedPhrase);
        if (distance < minDistance) {
          minDistance = distance;
          bestStartIndex = i;
        }
      }

      if (bestStartIndex !== -1) {
        const similarityRatio = 1 - minDistance / windowSize;
        if (similarityRatio > 0.8) {
          processingItems.push({
            startIndex: bestStartIndex,
            endIndex: bestStartIndex + windowSize,
            type: "phrase_match",
            item: phraseToItemMap.get(normalizedPhrase.toLowerCase()),
            originalPhrase: phrase,
            matchLength: windowSize,
            similarityRatio,
          });
        }
      }
    });

    // Sort by start index and remove overlaps
    processingItems.sort((a, b) => a.startIndex - b.startIndex);

    const nonOverlappingItems: ProcessingItem[] = [];
    let lastEndIndex = -1;

    for (const item of processingItems) {
      if (item.startIndex >= lastEndIndex) {
        nonOverlappingItems.push(item);
        lastEndIndex = item.endIndex;
      } else if (
        item.type === "phrase_match" &&
        item.similarityRatio &&
        nonOverlappingItems[nonOverlappingItems.length - 1]?.similarityRatio &&
        item.similarityRatio >
          nonOverlappingItems[nonOverlappingItems.length - 1].similarityRatio!
      ) {
        // Replace with better match
        nonOverlappingItems[nonOverlappingItems.length - 1] = item;
        lastEndIndex = item.endIndex;
      }
    }

    // Build the result
    const result = [];
    let currentIndex = 0;

    nonOverlappingItems.forEach((processingItem) => {
      // Add text before this item
      if (processingItem.startIndex > currentIndex) {
        result.push(text.substring(currentIndex, processingItem.startIndex));
      }

      if (processingItem.type === "word_before_deletion") {
        // Handle word_before deletion
        const uniqueKey = `${processingItem.item.id_long}-${processingItem.startIndex}`;
        result.push(
          <span key={`deleted-container-${uniqueKey}`}>
            <DeleteButton
              onClick={(e) => {
                e.stopPropagation();
                toggleDeletedText(uniqueKey);
              }}
              title={
                showingDeletedText[uniqueKey]
                  ? "Hide deleted text"
                  : "Show deleted text"
              }
            >
              {showingDeletedText[uniqueKey] ? "−" : "x"}
            </DeleteButton>
            {showingDeletedText[uniqueKey] && (
              <DeletedContent
                ref={hoveredItemKey === uniqueKey ? highlightRef : null}
                onMouseEnter={() => handleMouseEnter(uniqueKey)}
                onMouseLeave={handleMouseLeave}
              >
                {processingItem.item.text_deleted}
                <Tooltip
                  ref={hoveredItemKey === uniqueKey ? tooltipRef : null}
                  isVisible={hoveredItemKey === uniqueKey}
                >
                  <TooltipContent>
                    <IdLink
                      onClick={(e) => {
                        if (fileId) {
                          navigateToAmendment(
                            processingItem.item.amendment_id,
                            fileId,
                            e
                          );
                        }
                      }}
                      title="Go to amendment details"
                    >
                      {processingItem.item.id_long}
                    </IdLink>
                  </TooltipContent>
                </Tooltip>
              </DeletedContent>
            )}
          </span>
        );
        currentIndex = processingItem.startIndex;
      } else if (
        processingItem.type === "phrase_match" &&
        processingItem.item
      ) {
        // Handle phrase match
        const matchedText = text.substring(
          processingItem.startIndex,
          processingItem.endIndex
        );
        const uniqueKey = `${processingItem.item.id_long}-${processingItem.startIndex}`;
        const isShowingOriginal = showingOriginalText[uniqueKey];
        const isShowingDeleted = showingDeletedText[uniqueKey];
        const hasOriginalText = !!processingItem.item.text_replaced;
        const hasDeletedText = processingItem.item.type === "deleted";
        const displayText =
          isShowingOriginal && hasOriginalText
            ? processingItem.item.text_replaced
            : matchedText;
        const TextComponent =
          isShowingOriginal && hasOriginalText ? DeletedText : AddedText;

        result.push(
          <TextComponent
            key={uniqueKey}
            ref={hoveredItemKey === uniqueKey ? highlightRef : null}
            onMouseEnter={() => handleMouseEnter(uniqueKey)}
            onMouseLeave={handleMouseLeave}
          >
            {displayText}
            {hasDeletedText && !processingItem.item.word_before && (
              <>
                <DeleteButton
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleDeletedText(uniqueKey);
                  }}
                  title="Show deleted text"
                >
                  +
                </DeleteButton>
                {isShowingDeleted && (
                  <DeletedContent>
                    {processingItem.item.text_deleted}
                  </DeletedContent>
                )}
              </>
            )}
            <Tooltip
              ref={hoveredItemKey === uniqueKey ? tooltipRef : null}
              isVisible={hoveredItemKey === uniqueKey}
            >
              <TooltipContent>
                <IdLink
                  onClick={(e) => {
                    if (fileId) {
                      navigateToAmendment(
                        processingItem.item.amendment_id,
                        fileId,
                        e
                      );
                    }
                  }}
                  title="Go to amendment details"
                >
                  {processingItem.item.id_long}
                </IdLink>
                {hasOriginalText && (
                  <SwitchIcon
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleOriginalText(uniqueKey);
                    }}
                    title={
                      isShowingOriginal
                        ? "Show added text"
                        : "Show original text"
                    }
                  >
                    ⇄
                  </SwitchIcon>
                )}
              </TooltipContent>
            </Tooltip>
          </TextComponent>
        );
        currentIndex = processingItem.endIndex;
      }
    });

    // Add any remaining text
    if (currentIndex < text.length) {
      result.push(text.substring(currentIndex));
    }

    return result;
  };

  // Debug logging for rendering path
  if (process.env.NODE_ENV === "development") {
    const isNewParagraph =
      new_paragraph || (article_name && article_name.includes("(nouveau)"));
    const isHTMLContent = isHTML(text);
    console.log("HighlightedParagraph: Rendering path decision", {
      isNewParagraph,
      isHTMLContent,
      articleName: article_name,
      textPreview: text.substring(0, 100) + "...",
    });
  }

  return (
    <>
      {new_paragraph || (article_name && article_name.includes("(nouveau)")) ? (
        <ParagraphContainer textAlign={textAlign}>
          <AddedText
            ref={hoveredItemKey === "new-paragraph" ? highlightRef : null}
            onMouseEnter={() => handleMouseEnter("new-paragraph")}
            onMouseLeave={handleMouseLeave}
          >
            {isHTML(text) ? (
              <div dangerouslySetInnerHTML={{ __html: text }} />
            ) : (
              text
            )}
            {paragraph_origin_id_long && (
              <Tooltip
                ref={hoveredItemKey === "new-paragraph" ? tooltipRef : null}
                isVisible={hoveredItemKey === "new-paragraph"}
              >
                <TooltipContent>
                  <IdLink
                    onClick={(e) => {
                      if (fileId) {
                        navigateToAmendment(
                          paragraph_origin_id_long,
                          fileId,
                          e
                        );
                      }
                    }}
                    title="Go to amendment details"
                  >
                    {paragraph_origin_id_long}
                  </IdLink>
                </TooltipContent>
              </Tooltip>
            )}
          </AddedText>
        </ParagraphContainer>
      ) : isHTML(text) ? (
        highlightHTMLContent(text, phrasesToHighlight)
      ) : (
        <ParagraphContainer textAlign={textAlign}>
          {highlightText(text, phrasesToHighlight)}
        </ParagraphContainer>
      )}
    </>
  );
};

export default HighlightedParagraph;
